import axios from 'axios';
import * as util from '../util/utils';

const EVENTS_URL = process.env.EVENTS_URL || '/api/events';

export const publishEvent = async eventData => {
    return axios({
        method: 'POST',
        url: EVENTS_URL,
        data: eventData
    });
};

const constructEventAction = (actionPrefix, assessmentInfo) => {
    let assessmentType = util.getPathParam(1).toUpperCase();

    // Guides use the same "assessment" route
    if (util.getPathParam(2).includes('guide')) {
        assessmentType = 'GUIDE';
    }

    let eventAction = actionPrefix;

    const actionSuffixMapping = {
        POLL: `_Inline Poll_${assessmentInfo.name}`,
        ASSESSMENT: `_Assessment_${assessmentInfo.name}`,
        GUIDE: `_Good Appointment Guide_${assessmentInfo.name}`
    };

    const actionSuffix = actionSuffixMapping[assessmentType] || actionSuffixMapping.ASSESSMENT;
    eventAction += actionSuffix;

    return eventAction;
};

const getQueryStringParam = paramName => {
    const url = new URL(window.location.href);
    const param = url.searchParams.get(paramName);

    return param;
};

const getFromBase64EncodedParam = paramName => {
    const param = getQueryStringParam(paramName);
    if (!param) {
        return null;
    }

    const decoded = atob(param);
    return decoded;
};

const getPillarId = () => {
    const { host } = window.location;
    const hostParts = host.split('.');

    const pillarMapping = {
        healthline: 2,
        medicalnewstoday: 5,
        greatist: 6,
        psychcentral: 7
    };

    for (const part of hostParts) {
        if (pillarMapping[part]) return pillarMapping[part];
    }

    return pillarMapping.healthline;
};

const getGaId = () => {
    let gaId = null;

    const gaCookieValue = util.getCookieValue('_ga');

    if (gaCookieValue) {
        const gaParts = gaCookieValue.split('.');
        if (gaParts.length > 3) {
            gaId = `${gaParts[2]}.${gaParts[3]}`;
        }
    }

    return gaId;
};

const getAdditionalProperties = () => {
    const context = {
        userAgent: window.navigator.userAgent,
        referrer: document.referrer,
        title: document.title,
        url: window.location.href
    };

    const cookies = util.getAllCookies();

    const additionalProperties = { ...context, ...cookies };

    return additionalProperties;
};

const createBaseEvent = (assessmentInfo, options = {}) => {
    const { inlineParentMetadata } = options;

    const trackingDataFromQueryString = getFromBase64EncodedParam('td');
    let k1;
    let k2;
    let micrositeId;
    if (trackingDataFromQueryString) {
        const trackingDataParsed = JSON.parse(trackingDataFromQueryString);

        if (trackingDataParsed.k1) {
            k1 = trackingDataParsed.k1;
        }
        if (trackingDataParsed.k2) {
            k2 = trackingDataParsed.k2;
        }
        if (trackingDataParsed.msiteId) {
            micrositeId = trackingDataParsed.msiteId;
        }
    }

    const event = {
        eventCategory: 'Assessment',
        eventAction: options.eventAction,
        eventLabel: options.eventLabel || assessmentInfo.id,
        eventId: 0,
        apsessionid: '',
        memberData: {
            gaid: getGaId(),
            identifiers: {},
            pillarId: getPillarId()
        },
        trackingData: {
            trackingCodeId: getQueryStringParam('tc'),
            customTc: getQueryStringParam('subid'),
            subCustomTc:
                (inlineParentMetadata &&
                    inlineParentMetadata.ads &&
                    inlineParentMetadata.ads.url) ||
                getQueryStringParam('subid2') ||
                getQueryStringParam('sctc'),
            k1:
                (inlineParentMetadata && inlineParentMetadata.ads && inlineParentMetadata.ads.k1
                    ? inlineParentMetadata.ads.k1
                    : null) || k1,
            k2:
                (inlineParentMetadata && inlineParentMetadata.ads && inlineParentMetadata.ads.k2
                    ? inlineParentMetadata.ads.k2
                    : null) || k2,
            msiteid:
                (inlineParentMetadata &&
                inlineParentMetadata.ads &&
                inlineParentMetadata.ads.msiteID
                    ? inlineParentMetadata.ads.msiteID
                    : null) || micrositeId
        },
        offerData: {},
        pathData: {
            landingPageId: process.env.QUIZLER_LANDINGPAGE_ID
        },
        additionalProperties: getAdditionalProperties()
    };

    return event;
};

export const eventByType = async (eventType, assessmentId, options = {}) => {
    const event = createBaseEvent({id: assessmentId}, options);
    event.eventType = eventType;

    return publishEvent(event);
};

export const answerSelected = async (answer, question, assessmentInfo, options = {}) => {
    const event = createBaseEvent(assessmentInfo, options);
    event.eventType = 'answer_clicked';

    event.eventAction = constructEventAction('AnswerClicked', assessmentInfo);

    event.eventLabel = `${question}_${answer}`;

    event.question = question;
    if (options.questionId != null) {
        event.questionId = options.questionId;
        event.eventLabel = `${options.questionId}_${event.eventLabel}`;
    }

    event.answer = answer;
    if (options.answerType != null) {
        event.answerType = options.answerType;
    }

    event.assessmentName = assessmentInfo.name;
    if (options.appType != null) {
        event.pathData.formType = options.appType;
    }

    return publishEvent(event);
};

export const resultClicked = async (question, assessmentInfo, options = {}) => {
    const event = createBaseEvent(assessmentInfo, options);
    event.eventType = 'result_clicked';

    event.eventAction = constructEventAction('ResultClicked', assessmentInfo);
    event.eventLabel = options.text || `${question}`;

    event.question = question;
    event.assessmentName = assessmentInfo.name;

    return publishEvent(event);
};

export const articleSelected = async (answer, question, assessmentInfo, options = {}) => {
    const event = createBaseEvent(assessmentInfo, options);
    event.eventType = 'article_clicked';

    event.eventAction = constructEventAction('ArticleClicked', assessmentInfo);
    event.eventLabel = `${question}_${answer}_${options.position}_${options.destinationUrl}`;

    event.question = question;
    event.answer = answer;
    event.assessmentName = assessmentInfo.name;

    return publishEvent(event);
};

export const expandClicked = async (assessmentInfo, options = {}) => {
    const event = createBaseEvent(assessmentInfo, options);
    event.eventType = 'expand_clicked';

    event.eventAction = constructEventAction('ExpandClicked', assessmentInfo);
    event.eventLabel = options.text || '+';

    event.assessmentName = assessmentInfo.name;

    return publishEvent(event);
};

export const emailSubmitted = async (assessmentInfo, options = {}) => {
    const event = createBaseEvent(assessmentInfo, options);
    event.eventType = 'email_submitted';

    event.eventAction = constructEventAction('EmailSubmitted', assessmentInfo);
    event.eventLabel = options.text;

    event.assessmentName = assessmentInfo.name;

    return publishEvent(event);
};

export const resultsSubmitted = async (answer, question, assessmentInfo, options = {}) => {
    const event = createBaseEvent(assessmentInfo, options);
    event.eventType = 'result_submitted';

    event.eventAction = constructEventAction('ResultSubmitted', assessmentInfo);
    event.eventLabel = `${options.position}_${question}_${answer}`;

    event.assessmentName = assessmentInfo.name;

    return publishEvent(event);
};

export const assessmentStarted = async (assessmentInfo, options = {}) => {
    const event = createBaseEvent(assessmentInfo, options);
    event.eventType = 'assessment_started';

    event.eventAction = constructEventAction('AssessmentStarted', assessmentInfo);
    event.eventLabel = options.text;

    event.assessmentName = assessmentInfo.name;

    return publishEvent(event);
};

export const sendFreeResponseEvent = async (answer, question, assessmentInfo) => {
    const event = {
        eventType: 'free_response',
        question,
        answer,
        assessmentName: assessmentInfo.internalName
    };

    return publishEvent(event);
};
